import { useState, useEffect } from "react";
import { getNamespaceTranslations } from "../lib/questionnaire-translations";
import { Button } from "./ui/button";
import { Header } from "./Header";
import { ProgressBar } from "./ui/ProgressBar";
import type { ProgressSectionId } from "../lib/progress-calculator";

interface DoseSelectionProps {
  onContinue: (selectedDose: string) => void;
  onBack: () => void;
  onClose?: () => void;
  language?: "en" | "es";
  logoUrl?: string;
  productOptions?: any;
  productVariants?: any;
  productTitle?: string;
  productTemplate?: string;
  currentStep?: number;
  totalSteps?: number;
  sectionIndex?: number;
  totalSections?: number;
  sectionId?: ProgressSectionId;
  chosenDetails: string;
}

export function DoseSelection({
  onContinue,
  onBack,
  onClose,
  language = "en",
  logoUrl,
  productOptions,
  productVariants,
  productTemplate,
  productTitle,
  currentStep,
  totalSteps,
  sectionIndex,
  totalSections,
  sectionId,
  chosenDetails
}: DoseSelectionProps) {
  const t = getNamespaceTranslations(language, "productSelection");
  const [selectedDose, setSelectedDose] = useState<string>(chosenDetails);
  const [selectionError, setSelectionError] = useState("");
  const doseT = t?.doseSelection;

  const handleContinue = () => {
    if (!selectedDose) {
      setSelectionError(doseT?.selectionRequired || "This field is required");
      document.querySelector<HTMLElement>('[data-testid="dose_radio-1"]')?.focus();
      return;
    }
    setSelectionError("");
    onContinue(selectedDose);
  };

  // Get dose options from Shopify product options (which have translated values from @inContext)
  // Look for an option named "Dose", "Dosis", "Dosage", or similar
  const doseOption = productOptions?.find((opt: any) =>
    opt.name.toLowerCase().includes('dose') ||
    opt.name.toLowerCase().includes('dosis') ||
    opt.name.toLowerCase().includes('dosage') ||
    opt.name.toLowerCase().includes('strength')
  );

  // Get the translated dose values directly from product options
  const doseValues: string[] = doseOption?.values || [];

  // Build variant data with metafields
  const variantData = doseValues.map((doseValue: string) => {
    // Find the variant that matches this dose value
    const variant = productVariants?.edges?.find((edge: any) => {
      const doseOpt = edge.node.selectedOptions?.find(
        (opt: any) => opt.name === doseOption?.name
      );
      return doseOpt?.value === doseValue;
    })?.node;

    // Extract metafields
    const badge = variant?.metafields?.find((mf: any) => mf?.key === 'variant_badge')?.value || null;
    const description = variant?.metafields?.find((mf: any) => mf?.key === 'variant_description')?.value || null;
    const ingredientsMetafield = variant?.metafields?.find((mf: any) => mf?.key === 'variant_ingredients');

    // Parse ingredients from metaobject references
    let ingredients: Array<{name: string; dosage: string}> = [];
    if (ingredientsMetafield?.references?.edges) {
      ingredients = ingredientsMetafield.references.edges.map((edge: any) => {
        const fields = edge.node?.fields || [];
        const nameField = fields.find((f: any) => f.key === 'name');
        const dosageField = fields.find((f: any) => f.key === 'dosage');

        return {
          name: nameField?.value || '',
          dosage: dosageField?.value || '',
        };
      });
    }

    return {
      value: doseValue,
      badge,
      description,
      ingredients,
    };
  });

  // Auto-select first variant on mount
  useEffect(() => {
    if (doseValues.length > 0 && !selectedDose) {
      setSelectedDose(doseValues[0]);
    }
  }, [doseValues, selectedDose]);

  if (!doseT) {
    return null;
  }

  if (doseValues.length === 0) {
    console.warn('[DoseSelection] No dose options found in product');
    return null;
  }

  // Determine if we have a single variant (no choice needed)
  const isSingleVariant = doseValues.length === 1;
  const displayTitle = isSingleVariant && doseT.titleSingleVariant ? doseT.titleSingleVariant : doseT.title;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header logoUrl={logoUrl} onBack={onBack} onClose={onClose} />

      <div className="flex-1 flex flex-col bg-gradient-to-b from-[#FAFAFA] to-[#E8F0F3]">
        {/* Progress Bar */}
        <div className="px-4 sm:px-6 pt-[30px]">
          <div className="max-w-[640px] mx-auto">
            {currentStep !== undefined &&
              totalSteps !== undefined &&
              sectionIndex !== undefined &&
              totalSections !== undefined &&
              sectionId !== undefined && (
              <ProgressBar
                currentStep={currentStep}
                totalSteps={totalSteps}
                sectionIndex={sectionIndex}
                totalSections={totalSections}
                sectionId={sectionId}
                language={language}
              />
            )}
          </div>
        </div>

        {/* Content */}
        <div className="px-4 sm:px-6 pt-8 pb-[30px]">
          <div className="max-w-[640px] mx-auto">
            {/* Title */}
            <div className="mb-6">
              <h1
                id="dose-selection-title"
                className="text-[24px] sm:!text-[30px] leading-[30px] sm:!leading-[36px] font-normal text-black mb-2"
                style={{ fontFamily: "TT Commons Pro" }}
              >
                {displayTitle}
              </h1>
              {doseT.subtitle && (
                <p
                  id="dose-selection-description"
                  className="text-base leading-[22px] text-gray-500"
                  style={{ fontFamily: "TT Commons Pro" }}
                >
                  {doseT.subtitle}
                </p>
              )}
            </div>

            {/* Dose Options */}
            <div
              role="radiogroup"
              aria-labelledby="dose-selection-title"
              aria-describedby={doseT.subtitle ? "dose-selection-description" : undefined}
              aria-required="true"
              className="flex flex-col gap-4 mb-6"
            >
              {variantData.map((variant: any, index: number) => {
                const isSelected = selectedDose === variant.value;

                return (
                  <button
                    key={`${doseOption?.name}-${index}`}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    onClick={() => {
                      setSelectedDose(variant.value);
                      if (selectionError) setSelectionError("");
                    }}
                    data-testid={`dose_radio-${index + 1}`}
                    className={`w-full text-left transition-all bg-white rounded-[6px] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2D55FB] focus-visible:ring-offset-2 ${
                      isSelected
                        ? "border border-black"
                        : selectionError
                          ? "border border-[#E34D52]"
                          : "border border-[#E0E0E0]"
                    }`}
                  >
                    {/* Title, Badge, and Description Container with padding */}
                    <div className="p-4">
                      {/* Title and Badge Row */}
                      <div className={`flex items-center justify-between ${variant.description ? 'mb-4' : ''}`}>
                        <h3
                          className="text-black text-base leading-6 font-normal"
                          style={{ fontFamily: "TT Commons Pro" }}
                        >
                          {productTitle} {variant.value}
                        </h3>
                        {variant.badge && (
                          <span
                            className="ml-2 px-2.5 rounded-[20px] text-center text-sm leading-6 font-semibold"
                            style={{
                              fontFamily: "TT Commons Pro",
                              background: "#3CC34E1F"
                            }}
                          >
                            {variant.badge}
                          </span>
                        )}
                      </div>

                      {/* Description */}
                      {variant.description && (
                        <p
                          className="text-black text-sm leading-5 font-normal italic m-0"
                          style={{ fontFamily: "TT Commons Pro" }}
                        >
                          {variant.description}
                        </p>
                      )}
                    </div>

                    {/* Ingredients Section - Only show when selected */}
                    {isSelected && variant.ingredients && variant.ingredients.length > 0 && (
                      <div className="bg-[#F7F1F1] rounded-[6px] p-4" role="region" aria-label="Ingredients">
                        <div className="flex flex-col gap-3">
                          {variant.ingredients.map((ingredient: {name: string; dosage: string}, idx: number) => (
                            <div
                              key={idx}
                              className="text-base leading-6"
                              style={{ fontFamily: "TT Commons Pro" }}
                            >
                              <span className="font-normal">{ingredient.name}</span>{" "}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* How to choose your strength button */}
            <button
              type="button"
              data-strength-modal-trigger
              aria-label={productTemplate === 'deso' ? doseT.howToChooseDeso : doseT.howToChoose}
              data-testid="dose-how_to_choose"
              className="w-full flex items-center justify-between p-4 border border-[#E0E0E0] bg-[#F7F1F1] rounded-[6px] mb-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2D55FB] focus-visible:ring-offset-2"
            >
              <span
                className="text-black text-base leading-6 font-normal"
                style={{ fontFamily: 'TT Commons Pro' }}
              >
                {productTemplate === 'deso' ? doseT.howToChooseDeso : doseT.howToChoose}
              </span>
              <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                <path d="M8.375 1.5C6.03125 1.5 3.90625 2.75 2.71875 4.75C1.5625 6.78125 1.5625 9.25 2.71875 11.25C3.90625 13.2812 6.03125 14.5 8.375 14.5C10.6875 14.5 12.8125 13.2812 14 11.25C15.1562 9.25 15.1562 6.78125 14 4.75C12.8125 2.75 10.6875 1.5 8.375 1.5ZM8.375 16C5.5 16 2.875 14.5 1.4375 12C0 9.53125 0 6.5 1.4375 4C2.875 1.53125 5.5 0 8.375 0C11.2188 0 13.8438 1.53125 15.2812 4C16.7188 6.5 16.7188 9.53125 15.2812 12C13.8438 14.5 11.2188 16 8.375 16ZM7.125 10.5H7.875V8.5H7.125C6.6875 8.5 6.375 8.1875 6.375 7.75C6.375 7.34375 6.6875 7 7.125 7H8.625C9.03125 7 9.375 7.34375 9.375 7.75V10.5H9.625C10.0312 10.5 10.375 10.8438 10.375 11.25C10.375 11.6875 10.0312 12 9.625 12H7.125C6.6875 12 6.375 11.6875 6.375 11.25C6.375 10.8438 6.6875 10.5 7.125 10.5ZM8.375 6C7.8125 6 7.375 5.5625 7.375 5C7.375 4.46875 7.8125 4 8.375 4C8.90625 4 9.375 4.46875 9.375 5C9.375 5.5625 8.90625 6 8.375 6Z" fill="black"/>
              </svg>
            </button>

            {/* Continue Button — always full colour; validate on submit */}
            {selectionError && (
              <p className="text-[#E34D52] text-sm mb-3" role="alert" style={{ fontFamily: "TT Commons Pro" }}>
                {selectionError}
              </p>
            )}
            <Button
              type="button"
              onClick={handleContinue}
              aria-label={doseT.continue}
              data-testid="dose-continue"
              className="w-full h-[50px] text-white text-lg leading-6 font-medium rounded-full flex items-center justify-center transition-colors bg-[#2D55FB] hover:bg-[#2347E8]"
            >
              {doseT.continue}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
